// src/pages/Profile/components/ProfileNetwork.tsx
import React, { useState, useEffect } from 'react';
import {
   Box,
   Typography,
   Grid,
   Card,
   CardContent,
   Avatar,
   Chip,
   Button,
   Divider,
   Paper,
   CircularProgress,
   Alert,
   TextField,
   InputAdornment,
   Tabs,
   Tab,
   IconButton,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   ListItemAvatar,
   ListItemText,
   List,
   ListItem,
} from '@mui/material';
import {
   People as PeopleIcon,
   Search as SearchIcon,
   PersonAdd as PersonAddIcon,
   CheckCircle as CheckCircleIcon,
   Cancel as CancelIcon,
   HourglassEmpty as HourglassEmptyIcon,
   Send as SendIcon,
   GroupAdd as GroupAddIcon,
   FilterList as FilterListIcon,
   Home as HomeIcon,
   Group as GroupIcon,
   BusinessCenter as WorkIcon,
   Favorite as LoveIcon,
   Person as PersonIcon,
} from '@mui/icons-material';
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import {
   getNetworkRequests,
   acceptNetworkRequest,
   declineNetworkRequest,
   cancelNetworkRequest,
   getNetworkStats,
} from '../../../services/networkService';
import {
   RelationshipType,
   relationshipLabels,
   relationshipCategories,
} from '../../../types/Relationship';
import { generateFallbackAvatar } from '../../../utils/avatarUtils';
import { useNavigate } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material';

interface NetworkConnection {
   id: string;
   userId: string;
   displayName: string;
   email: string;
   photoURL?: string;
   relationship?: {
      type: RelationshipType;
      label?: string;
      notes?: string;
   };
   connectionDate?: Date;
   lastInteraction?: Date;
   status: 'accepted' | 'pending' | 'declined';
   //  direction: 'incoming' | 'outgoing';
   connectionSource?: 'direct' | 'event_invitation' | 'trip_invitation';
   //  connectionSourceName?: string;
}

interface NetworkRequest {
   id?: string;
   fromUserId: string;
   fromUserName: string;
   fromUserEmail: string;
   fromUserPhoto?: string | null;
   toUserId: string | null;
   toUserName: string | null | undefined;
   toUserEmail: string | null | undefined;
   toUserPhoto?: string | null;
   status: 'pending' | 'accepted' | 'declined';
   message?: string;
   relationshipType?: RelationshipType;
   relationshipLabel?: string;
   createdAt: Date;
   updatedAt?: Date;
   eventId?: string;
   eventTitle?: string;
}

interface ProfileNetworkProps {
   userId: string;
}

const ProfileNetwork: React.FC<ProfileNetworkProps> = ({ userId }) => {
   const navigate = useNavigate();
   const [activeTab, setActiveTab] = useState<number>(0);
   const [connections, setConnections] = useState<NetworkConnection[]>([]);
   const [filteredConnections, setFilteredConnections] = useState<NetworkConnection[]>([]);
   const [requests, setRequests] = useState<NetworkRequest[]>([]);
   const [loading, setLoading] = useState<boolean>(true);
   const [error, setError] = useState<string | null>(null);
   const [successMessage, setSuccessMessage] = useState<string | null>(null);
   const [searchQuery, setSearchQuery] = useState<string>('');
   const [relationshipFilter, setRelationshipFilter] = useState<string>('all');
   const [processingRequestId, setProcessingRequestId] = useState<string | null>(null);
   const [totalConnections, setTotalConnections] = useState<number>(0);
   const [totalConnectionsBySourceTrip, setTotalConnectionsBySourceTrip] = useState<number>(0);
   const [totalConnectionsBySourceEvent, setTotalConnectionsBySourceEvent] = useState<number>(0);
   const [totalConnectionsByDirect, setTotalConnectionsByDirect] = useState<number>(0);
   const [totalTrips, setTotalTrips] = useState<number>(0);
   const [totalEvents, setTotalEvents] = useState<number>(0);

   // Fetch network connections and requests
   useEffect(() => {
      if (!userId) return;

      const fetchNetwork = async () => {
         setLoading(true);
         setError(null);

         try {
            // Fetch network statistics from the API
            const stats = await getNetworkStats(userId);
            
            setTotalConnections(stats.totalConnections);
            setTotalConnectionsBySourceTrip(stats.connectionsByTrip);
            setTotalConnectionsBySourceEvent(stats.connectionsByEvent);
            setTotalConnectionsByDirect(stats.connectionsByDirect);
            setTotalTrips(stats.totalTrips);
            setTotalEvents(stats.totalEvents);

            // Also fetch the actual connections for display (if needed)
            const relationshipsQuery = query(
               collection(db, 'relationship_network'),
               where('userId', '==', userId),
               where('isNetworkConfirmed', '==', true)
            );

            const relationshipsSnapshot = await getDocs(relationshipsQuery);
            const relationshipsData: NetworkConnection[] = [];

            for (const docSnapshot of relationshipsSnapshot.docs) {
               const relationshipData = docSnapshot.data();
               const otherUserId = relationshipData.relationToUserId;

               if (otherUserId) {
                  try {
                     const userDoc = await getDoc(doc(db, 'users', otherUserId));

                     if (userDoc.exists()) {
                        const userData = userDoc.data();
                        const relationship = {
                           type: relationshipData.relationType,
                           name: userData.displayName,
                           email: userData.email,
                           photoURL: userData.photoURL,
                           connectionDate: relationshipData.createdAt?.toDate(),
                           status: relationshipData.status,
                           connectionSource: relationshipData.source,
                           connectionSourceName: relationshipData.sourceId,
                           id: docSnapshot.id,
                           userId: otherUserId,
                           displayName: userData.displayName,
                           relationship: {
                              type: relationshipData.relationType,
                              label: relationshipData.relationLabel,
                              notes: relationshipData.notes,
                           },
                        };

                        relationshipsData.push(relationship);
                     }
                  } catch (err) {
                     console.error('Error fetching user data:', err);
                  }
               }
            }

            setConnections(relationshipsData);
         } catch (err) {
            console.error('Error fetching network data:', err);
            setError('Failed to load your network. Please try again.');
         } finally {
            setLoading(false);
         }
      };

      fetchNetwork();
   }, [userId]);

   // Apply filters when search query or relationship filter changes
   useEffect(() => {
      if (!connections.length) return;

      let filtered = [...connections];

      // Apply search filter
      if (searchQuery) {
         const query = searchQuery.toLowerCase();
         filtered = filtered.filter(
            (connection) =>
               connection.displayName.toLowerCase().includes(query) ||
               connection.email.toLowerCase().includes(query)
         );
      }

      // Apply relationship filter
      if (relationshipFilter && relationshipFilter !== 'all') {
         if (relationshipFilter === 'uncategorized') {
            filtered = filtered.filter((connection) => !connection.relationship);
         } else {
            filtered = filtered.filter((connection) => {
               if (!connection.relationship) return false;

               // Check if relationship type belongs to selected category
               const types =
                  relationshipCategories[relationshipFilter as keyof typeof relationshipCategories];
               return types && types.includes(connection.relationship.type);
            });
         }
      }

      setFilteredConnections(filtered);
   }, [connections, searchQuery, relationshipFilter]);

   const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
   };

   const handleRelationshipFilterChange = (event: SelectChangeEvent<string>) => {
      setRelationshipFilter(event.target.value);
   };

   // Render network statistics
   const renderNetworkStats = () => {
      // Get counts for different relationship categories
      const counts = {
         total: totalConnections,
         totalConnectionsBySourceTrip: totalConnectionsBySourceTrip,
         totalConnectionsBySourceEvent: totalConnectionsBySourceEvent,
         totalConnectionsByDirect: totalConnectionsByDirect,
         totalTrips: totalTrips,
         totalEvents: totalEvents,
      };

      return (
         <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
               <Card sx={{ textAlign: 'center', p: 2 }}>
                  <PeopleIcon color='primary' sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant='h4'>{counts.total}</Typography>
                  <Typography variant='body2' color='text.secondary'>
                     Total Connections
                  </Typography>
               </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
               <Card sx={{ textAlign: 'center', p: 2 }}>
                  <HomeIcon color='secondary' sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant='h4'>{counts.totalConnectionsBySourceTrip}</Typography>
                  <Typography variant='body2' color='text.secondary'>
                     Through Trips
                  </Typography>
               </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
               <Card sx={{ textAlign: 'center', p: 2 }}>
                  <GroupIcon color='success' sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant='h4'>{counts.totalConnectionsBySourceEvent}</Typography>
                  <Typography variant='body2' color='text.secondary'>
                     Through Events
                  </Typography>
               </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
               <Card sx={{ textAlign: 'center', p: 2 }}>
                  <PersonIcon color='info' sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant='h4'>{counts.totalConnectionsByDirect}</Typography>
                  <Typography variant='body2' color='text.secondary'>
                     Direct Connections
                  </Typography>
               </Card>
            </Grid>
         </Grid>
      );
   };

   // Render filters
   const renderFilters = () => {
      return (
         <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
               placeholder='Search connections...'
               size='small'
               value={searchQuery}
               onChange={handleSearchChange}
               InputProps={{
                  startAdornment: (
                     <InputAdornment position='start'>
                        <SearchIcon />
                     </InputAdornment>
                  ),
               }}
               sx={{ flexGrow: 1, minWidth: 200 }}
            />

            <FormControl size='small' sx={{ minWidth: 200 }}>
               <InputLabel id='relationship-filter-label'>Relationship</InputLabel>
               <Select
                  labelId='relationship-filter-label'
                  value={relationshipFilter}
                  onChange={handleRelationshipFilterChange}
                  label='Relationship'
               >
                  <MenuItem value='all'>All Connections</MenuItem>
                  <MenuItem value='Family'>Family</MenuItem>
                  <MenuItem value='Friends'>Friends</MenuItem>
                  <MenuItem value='Work'>Work</MenuItem>
                  <MenuItem value='Romantic'>Romantic</MenuItem>
                  <MenuItem value='Other'>Other</MenuItem>
                  <MenuItem value='uncategorized'>Uncategorized</MenuItem>
               </Select>
            </FormControl>
         </Box>
      );
   };

   // Render network connections in a grid layout
   //  const renderConnections = () => {
   //     if (filteredConnections.length === 0) {
   //        return (
   //           <Paper sx={{ p: 4, textAlign: 'center' }}>
   //              <Typography variant='h6' gutterBottom>
   //                 No connections found
   //              </Typography>
   //              <Typography color='text.secondary' paragraph>
   //                 {searchQuery || relationshipFilter !== 'all'
   //                    ? 'No connections match your filter criteria.'
   //                    : "You don't have any connections yet. Start by connecting with people!"}
   //              </Typography>
   //              <Button
   //                 variant='contained'
   //                 startIcon={<GroupAddIcon />}
   //                 onClick={() => navigate('/people')}
   //                 sx={{ mt: 2 }}
   //              >
   //                 Find People
   //              </Button>
   //           </Paper>
   //        );
   //     }

   //     return (
   //        <Grid container spacing={2}>
   //           {filteredConnections.map((connection) => (
   //              <Grid item xs={12} sm={6} md={4} key={connection.id}>
   //                 <Card
   //                    sx={{
   //                       height: '100%',
   //                       display: 'flex',
   //                       flexDirection: 'column',
   //                       transition: 'transform 0.2s ease-in-out',
   //                       '&:hover': {
   //                          transform: 'translateY(-4px)',
   //                          boxShadow: 3,
   //                       },
   //                    }}
   //                 >
   //                    <CardContent sx={{ flexGrow: 1 }}>
   //                       <Box sx={{ display: 'flex', mb: 2 }}>
   //                          <Avatar
   //                             src={
   //                                connection.photoURL ||
   //                                generateFallbackAvatar(connection.displayName)
   //                             }
   //                             alt={connection.displayName}
   //                             sx={{ width: 60, height: 60, mr: 2 }}
   //                          />
   //                          <Box>
   //                             <Typography variant='h6'>{connection.displayName}</Typography>
   //                             <Typography variant='body2' color='text.secondary'>
   //                                {connection.email}
   //                             </Typography>
   //                             {connection.connectionDate && (
   //                                <Typography variant='caption' color='text.secondary'>
   //                                   Connected {connection.connectionDate.toLocaleDateString()}
   //                                </Typography>
   //                             )}
   //                          </Box>
   //                       </Box>

   //                       {connection.relationship && (
   //                          <Box sx={{ mb: 2 }}>
   //                             <Chip
   //                                icon={getRelationshipIcon(connection.relationship)}
   //                                label={
   //                                   connection.relationship.label ||
   //                                   relationshipLabels[connection.relationship.type]
   //                                }
   //                                color='primary'
   //                                variant='outlined'
   //                                size='small'
   //                             />
   //                          </Box>
   //                       )}

   //                       {connection.connectionSource &&
   //                          connection.connectionSource !== 'direct' && (
   //                             <Typography variant='body2' color='text.secondary'>
   //                                Met via{' '}
   //                                {connection.connectionSource === 'event' ? 'event' : 'trip'}:
   //                                {connection.connectionSourceName &&
   //                                   ` "${connection.connectionSourceName}"`}
   //                             </Typography>
   //                          )}

   //                       <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
   //                          <Button
   //                             size='small'
   //                             variant='outlined'
   //                             onClick={() => navigate(`/people?view=${connection.userId}`)}
   //                          >
   //                             View Profile
   //                          </Button>
   //                          <Button
   //                             size='small'
   //                             variant='outlined'
   //                             startIcon={<SendIcon />}
   //                             onClick={() =>
   //                                navigate('/events/create', {
   //                                   state: { inviteEmail: connection.email },
   //                                })
   //                             }
   //                          >
   //                             Invite
   //                          </Button>
   //                       </Box>
   //                    </CardContent>
   //                 </Card>
   //              </Grid>
   //           ))}
   //        </Grid>
   //     );
   //  };

   // Render network requests
   //  const renderNetworkRequests = () => {
   //     // Filter requests by direction and status
   //     const incomingRequests = requests.filter(
   //        (r) => r.toUserId === userId && r.status === 'pending'
   //     );

   //     const outgoingRequests = requests.filter(
   //        (r) => r.fromUserId === userId && r.status === 'pending'
   //     );

   //     if (incomingRequests.length === 0 && outgoingRequests.length === 0) {
   //        return (
   //           <Paper sx={{ p: 4, textAlign: 'center' }}>
   //              <Typography variant='h6' gutterBottom>
   //                 No pending requests
   //              </Typography>
   //              <Typography color='text.secondary' paragraph>
   //                 You don't have any pending connection requests.
   //              </Typography>
   //              <Button
   //                 variant='contained'
   //                 startIcon={<GroupAddIcon />}
   //                 onClick={() => navigate('/people')}
   //                 sx={{ mt: 2 }}
   //              >
   //                 Find People
   //              </Button>
   //           </Paper>
   //        );
   //     }

   //     return (
   //        <Box>
   //           {incomingRequests.length > 0 && (
   //              <Box sx={{ mb: 4 }}>
   //                 <Typography variant='h6' gutterBottom>
   //                    Incoming Requests
   //                 </Typography>
   //                 <Paper>
   //                    <List>
   //                       {incomingRequests.map((request) => (
   //                          <ListItem
   //                             key={request.id}
   //                             divider
   //                             secondaryAction={
   //                                <Box sx={{ display: 'flex', gap: 1 }}>
   //                                   <Button
   //                                      size='small'
   //                                      variant='outlined'
   //                                      color='inherit'
   //                                      onClick={() => handleDeclineRequest(request.id!)}
   //                                      disabled={processingRequestId === request.id}
   //                                   >
   //                                      Decline
   //                                   </Button>
   //                                   <Button
   //                                      size='small'
   //                                      variant='contained'
   //                                      color='primary'
   //                                      onClick={() => handleAcceptRequest(request.id!)}
   //                                      disabled={processingRequestId === request.id}
   //                                      startIcon={
   //                                         processingRequestId === request.id ? (
   //                                            <CircularProgress size={16} color='inherit' />
   //                                         ) : null
   //                                      }
   //                                   >
   //                                      {processingRequestId === request.id
   //                                         ? 'Processing...'
   //                                         : 'Accept'}
   //                                   </Button>
   //                                </Box>
   //                             }
   //                          >
   //                             <ListItemAvatar>
   //                                <Avatar
   //                                   src={
   //                                      request.fromUserPhoto ||
   //                                      generateFallbackAvatar(request.fromUserName)
   //                                   }
   //                                >
   //                                   {request.fromUserName.charAt(0)}
   //                                </Avatar>
   //                             </ListItemAvatar>
   //                             <ListItemText
   //                                primary={
   //                                   <Typography variant='body1' fontWeight='medium'>
   //                                      {request.fromUserName}
   //                                   </Typography>
   //                                }
   //                                secondary={
   //                                   <>
   //                                      <Typography component='span' variant='body2'>
   //                                         {request.fromUserEmail}
   //                                      </Typography>
   //                                      {request.relationshipType && (
   //                                         <Chip
   //                                            size='small'
   //                                            label={
   //                                               request.relationshipLabel ||
   //                                               relationshipLabels[request.relationshipType]
   //                                            }
   //                                            sx={{ ml: 1 }}
   //                                         />
   //                                      )}
   //                                      {request.eventTitle && (
   //                                         <Typography
   //                                            component='div'
   //                                            variant='caption'
   //                                            color='primary'
   //                                            sx={{ mt: 0.5 }}
   //                                         >
   //                                            <strong>Met at:</strong> {request.eventTitle}
   //                                         </Typography>
   //                                      )}
   //                                      {request.message && (
   //                                         <Typography
   //                                            component='div'
   //                                            variant='caption'
   //                                            sx={{ mt: 0.5, fontStyle: 'italic' }}
   //                                         >
   //                                            "{request.message}"
   //                                         </Typography>
   //                                      )}
   //                                      <Typography
   //                                         component='div'
   //                                         variant='caption'
   //                                         sx={{ mt: 0.5 }}
   //                                      >
   //                                         Received{' '}
   //                                         {new Date(request.createdAt).toLocaleDateString()}
   //                                      </Typography>
   //                                   </>
   //                                }
   //                             />
   //                          </ListItem>
   //                       ))}
   //                    </List>
   //                 </Paper>
   //              </Box>
   //           )}

   //           {outgoingRequests.length > 0 && (
   //              <Box>
   //                 <Typography variant='h6' gutterBottom>
   //                    Sent Requests
   //                 </Typography>
   //                 <Paper>
   //                    <List>
   //                       {outgoingRequests.map((request) => (
   //                          <ListItem
   //                             key={request.id}
   //                             divider
   //                             secondaryAction={
   //                                <Button
   //                                   size='small'
   //                                   variant='outlined'
   //                                   color='error'
   //                                   onClick={() => handleCancelRequest(request.id!)}
   //                                   disabled={processingRequestId === request.id}
   //                                   startIcon={
   //                                      processingRequestId === request.id ? (
   //                                         <CircularProgress size={16} color='inherit' />
   //                                      ) : null
   //                                   }
   //                                >
   //                                   {processingRequestId === request.id ? 'Canceling...' : 'Cancel'}
   //                                </Button>
   //                             }
   //                          >
   //                             <ListItemAvatar>
   //                                <Avatar
   //                                   src={
   //                                      request.toUserPhoto ||
   //                                      (request.toUserName || request.toUserEmail
   //                                         ? generateFallbackAvatar(
   //                                              request.toUserName || request.toUserEmail || ''
   //                                           )
   //                                         : undefined)
   //                                   }
   //                                >
   //                                   {request.toUserName
   //                                      ? request.toUserName.charAt(0)
   //                                      : request.toUserEmail
   //                                        ? request.toUserEmail.charAt(0)
   //                                        : '?'}
   //                                </Avatar>
   //                             </ListItemAvatar>
   //                             <ListItemText
   //                                primary={
   //                                   <Typography variant='body1' fontWeight='medium'>
   //                                      {request.toUserName ||
   //                                         (request.toUserEmail
   //                                            ? request.toUserEmail.split('@')[0]
   //                                            : 'Unknown User')}
   //                                   </Typography>
   //                                }
   //                                secondary={
   //                                   <>
   //                                      <Typography component='span' variant='body2'>
   //                                         {request.toUserEmail}
   //                                      </Typography>
   //                                      {request.relationshipType && (
   //                                         <Chip
   //                                            size='small'
   //                                            label={
   //                                               request.relationshipLabel ||
   //                                               relationshipLabels[request.relationshipType]
   //                                            }
   //                                            sx={{ ml: 1 }}
   //                                         />
   //                                      )}
   //                                      {request.eventTitle && (
   //                                         <Typography
   //                                            component='div'
   //                                            variant='caption'
   //                                            color='secondary'
   //                                            sx={{ mt: 0.5 }}
   //                                         >
   //                                            <strong>Event:</strong> {request.eventTitle}
   //                                         </Typography>
   //                                      )}
   //                                      <Typography
   //                                         component='div'
   //                                         variant='caption'
   //                                         sx={{ mt: 0.5 }}
   //                                      >
   //                                         Sent {new Date(request.createdAt).toLocaleDateString()}
   //                                      </Typography>
   //                                   </>
   //                                }
   //                             />
   //                          </ListItem>
   //                       ))}
   //                    </List>
   //                 </Paper>
   //              </Box>
   //           )}
   //        </Box>
   //     );
   //  };

   return (
      <Box>
         {/* Header with statistics */}
         {renderNetworkStats()}

         {/* Status messages */}
         {error && (
            <Alert severity='error' sx={{ mb: 3 }}>
               {error}
            </Alert>
         )}

         {successMessage && (
            <Alert severity='success' sx={{ mb: 3 }}>
               {successMessage}
            </Alert>
         )}

         {/* Tab navigation */}
         {/* <Tabs 
        value={activeTab} 
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab 
          label="My Connections" 
          icon={<PeopleIcon />} 
          iconPosition="start"
        />
        <Tab 
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
              Pending Requests
              {requests.filter(r => r.status === 'pending').length > 0 && (
                <Chip
                  label={requests.filter(r => r.status === 'pending').length}
                  color="primary"
                  size="small"
                  sx={{
                    position: 'absolute',
                    right: -30,
                    height: 20,
                    minWidth: 20,
                    fontSize: '0.75rem'
                  }}
                />
              )}
            </Box>
          }
          icon={<HourglassEmptyIcon />} 
          iconPosition="start"
        />
      </Tabs>
      
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
        
          {activeTab === 0 && (
            <>
              {renderFilters()}
              {renderConnections()}
            </>
          )}
          
          
          {activeTab === 1 && renderNetworkRequests()}
        </>
      )} */}

         {/* Add to network button */}
         {/* <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
               variant='contained'
               size='large'
               startIcon={<PersonAddIcon />}
               onClick={() => navigate('/people')}
            >
               Find More Connections
            </Button>
         </Box> */}
      </Box>
   );
};

export default ProfileNetwork;
